<template lang="pug">
  table.table.table--bars
    thead
      tr.table__row.table__row--head
        th.table__cell {{ 'base.date' | translate }}
        th.table__cell {{ 'base.status' | translate }}
        th.table__cell {{ 'base.period' | translate }}
        th.table__cell {{ 'base.loading' | translate }}
        th.table__cell {{ 'base.ration' | translate }}
        th.table__cell {{ 'base.animal_groups' | translate }}
        th.table__cell {{ 'base.animal_cnt' | translate }}
        th.table__cell {{ 'base.starting_mass' | translate }}
        th.table__cell {{ 'base.weight' | translate }} ({{ 'base.kg' | translate }})#[br] {{ 'base.plan_fact' | translate }}
        th.table__cell {{ 'base.fresh_weight' | translate }}#[br] {{ 'base.plan_fact' | translate }}
        th.table__cell(v-if="type === 'load'") {{ 'base.loading_time' | translate }}
        th.table__cell(v-else) {{ 'base.uploading_time' | translate }}
        th.table__cell(v-if="type === 'load'") {{ 'reports.auger_rotations' | translate }}
    v-table-body(
      :cols="type === 'load' ? 12 : 11"
      :empty="!rows.length"
      :loading="loading")
      tr.table__row.table__row--hover(
        v-for="(item, index) in rows"
        :key="item.id"
        @click="$emit('select', item)")
        td.table__cell.nowrap {{ item.planned_on }}
        td.table__cell.nowrap
          status-label(:status="item.status")
        td.table__cell.nowrap {{ shortStr(item.loading_name) }}
        td.table__cell.nowrap {{ shortStr(item.name) }}
        td.table__cell.nowrap {{ shortStr(item.ration_name) }}
        td.table__cell.nowrap {{ shortStr(item.animal_group_label) }}
        td.table__cell.nowrap {{ item.animal_count }}
        td.table__cell.nowrap {{ item.mixer_balance }}
        td.table__cell.nowrap
          span.weight-badge(:class="getWeightColor(item, type)") {{ item.plan_weight }} / {{ item.fact_weight }}
        td.table__cell.nowrap {{ item.plan_per_cow }} / {{ item.fact_per_cow }}
        td.table__cell.nowrap {{ ( Math.round(item.mix_time/ 60))}} {{ 'base.min' | translate }}
        td.table__cell.nowrap(v-if="type === 'load'") {{ item.average_auger_rotations ? item.average_auger_rotations : '-' }}
</template>

<script>
import { formatDateUnified } from '@/util'
import { mapActions } from 'vuex'
import StatusLabel from './StatusLabel.vue'
import api from '@/api'

export default {
  name: 'GeneralTable',

  props: {
    loading: Boolean,
    type: {
      type: String,
      default: 'upload'
    },
    items: Array
  },

  components: {
    StatusLabel
  },

  data: () => ({
    rows: [],
    monitoringSettings: null
  }),

  async mounted () {
    if (!this.farmId) {
      console.warn('⚠️ Cannot load ingredients: no farm ID')
      return
    }
    try {
      const response = await api.monitoring.farms(this.farmId)
      this.monitoringSettings = response.data
    } catch (error) {
      console.error('Error loading monitoring settings:', error)
    }

    this.$nextTick(async () => {
      if (this.$route.path.includes('farm')) {
        const farmId = this.$route.params.farm_id
        const { timezone } = await this.getFarm(farmId)
        this.$store.dispatch('account/setTimezone', timezone)
        this.formateDate()
      }
    })
  },

  computed: {
    currentLang () {
      return this.$store.getters['account/lang']
    },
    farmId () {
      const farmId = this.$route.params.farm_id
      return farmId
    },
    allowViewMonitoringFarm () {
      return this.hasPermission('monitoring_farm', 'view')
    }
  },

  methods: {
    ...mapActions('farms', [
      'getFarm'
    ]),

    getWeightColor (item, type) {
      if (!this.allowViewMonitoringFarm) {
        return ''
      }

      if (!this.monitoringSettings) {
        return ''
      }

      const settings = type === 'load'
        ? this.monitoringSettings.load_weight
        : this.monitoringSettings.upload_weight

      if (!settings || !settings.enabled) {
        return ''
      }

      const planWeight = item.plan_weight
      const factWeight = item.fact_weight

      if (!planWeight || planWeight === 0 || !factWeight || factWeight === 0) {
        return ''
      }

      const deviationPercent = ((factWeight - planWeight) / planWeight) * 100
      const absDeviation = Math.abs(deviationPercent)

      let threshold = null

      if (settings.level === 'percent') {
        threshold = settings.percent
      } else if (settings.level === 'weight' && planWeight > 0) {
        threshold = (settings.weight / planWeight) * 100
      }

      if (threshold !== null && threshold !== undefined && threshold !== 0) {
        if (absDeviation <= threshold) {
          return 'weight-green'
        }

        return 'weight-red'
      }

      if (settings.green !== 0 && absDeviation <= settings.green) {
        return 'weight-green'
      }

      if (settings.red !== 0 && absDeviation >= settings.red) {
        return 'weight-red'
      }

      if (settings.yellow !== 0 && absDeviation < settings.yellow) {
        return 'weight-yellow'
      }

      return ''
    },

    formateDate () {
      this.rows = []
      let useGroupsValues = this.$route.path.includes('mixer-uploads')
      this.items.forEach(element => {
        let item = {}
        item.id = element.id
        item.name = element.name
        item.average_auger_rotations = element.schedule_mixer.average_auger_rotations
        item.mixer_balance = element.mixer_balance
        item.planned_on = formatDateUnified(element.planned_on)
        item.status = element.status
        item.loading_name = element.schedule_loading.name
        item.ration_name = element.schedule_ration.name
        item.animal_count = element.schedule_groups_list.animal_count
        item.animal_type_label = this.getArrLabel(element.schedule_groups_list.animal_types)
        item.animal_group_label = this.getArrLabel(element.schedule_groups_list.names)
        if (useGroupsValues) {
          item.plan_weight = +this.getArrCount(element.schedule_groups, 'plan_weight').toFixed(0)
          item.fact_weight = +this.getArrCount(element.schedule_groups, 'fact_weight').toFixed(0)
        } else {
          item.plan_weight = +this.getArrCount(element.schedule_ration.ingredients, 'plan_weight').toFixed(0)
          item.fact_weight = +this.getArrCount(element.schedule_ration.ingredients, 'fact_weight').toFixed(0)
        }
        item.plan_per_cow = (item.plan_weight / item.animal_count).toFixed(1)
        item.fact_per_cow = (item.fact_weight / item.animal_count).toFixed(1)

        if (this.type === 'load') {
          item.mix_time = this.getArrCount(element.schedule_ration.ingredients, 'time_load')
        } else item.mix_time = this.getArrCount(element.schedule_groups, 'time_load')

        this.rows.push(item)
      })
    },

    getArrLabel (array) {
      let label = ''
      array.forEach((element, index) => {
        label += element
        if (index < array.length - 1) {
          label += ', '
        }
      })
      return label
    },

    getArrCount (array, field) {
      let count = 0
      array.forEach(element => {
        count += element[field] ? element[field] : 0
      })
      return count
    },

    shortStr (str) {
      return str.length > 10 ? (str.substring(0, 10) + '...') : str
    }
  },

  watch: {
    items (v) {
      this.formateDate()
    },
    currentLang: {
      handler () {
        this.formateDate()
      }
    }
  }
}
</script>

<style scoped>
.table--bars tr:not(.table__row--head)>.table__cell {
  padding: 1.1rem 1rem;
}

.table__cell {
  padding: 1.8rem 1rem;
}

.weight-badge {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-weight: 500;
  min-width: 6rem;
  text-align: center;
}

.weight-green {
  background-color: #91EA91;
}

.weight-yellow {
  background-color: #FBE268;
}

.weight-red {
  background-color: #FF9191;
}
</style>